import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, Mail, ArrowRight, Home, Sparkles, Star } from 'lucide-react';

interface LocationState {
  applicationId?: string;
  analysis?: {
    overall: number;
    feedback: string;
  };
}

export default function ApplicationSubmitted() {
  const location = useLocation();
  const state = location.state as LocationState;
  const [applicationId, setApplicationId] = useState<string>('');
  const [analysis, setAnalysis] = useState<any>(null);

  useEffect(() => {
    if (state?.applicationId) {
      setApplicationId(state.applicationId);
      setAnalysis(state.analysis);
    } else {
      const lastApp = localStorage.getItem('lastApplication');
      if (lastApp) {
        const app = JSON.parse(lastApp);
        setApplicationId(app.id);
        setAnalysis(app.analysis);
      } else {
        setApplicationId('APP-' + Math.random().toString(36).substr(2, 9).toUpperCase());
      }
    }
  }, [state]);

  const renderStars = (score: number) => {
    return (
      <div className="flex items-center justify-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-4 w-4 ${
              star <= Math.round(score) ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#05070A] flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full bg-[#0A0C12] border-slate-800">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-20 h-20 bg-blue-500/10 rounded-full flex items-center justify-center">
              <CheckCircle2 className="h-10 w-10 text-blue-400" />
            </div>
          </div>
          <CardTitle className="text-3xl text-white">Application Submitted!</CardTitle>
          <CardDescription className="text-slate-400 text-lg">
            Your squad application is now under review
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          <div className="bg-slate-800/30 rounded-lg p-6 text-center">
            <p className="text-sm text-slate-400 mb-2">Application Reference</p>
            <p className="text-2xl font-mono text-blue-400">{applicationId}</p>
          </div>

          {analysis && (
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-6">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Sparkles className="h-5 w-5 text-purple-400" />
                <h3 className="text-lg font-semibold text-white">AI Initial Assessment</h3>
              </div>
              
              <div className="flex items-center justify-center gap-4 mb-3">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white">{analysis.overall}/5</div>
                  <p className="text-xs text-slate-400">Preliminary Score</p>
                </div>
                <div className="text-left">
                  {renderStars(analysis.overall)}
                </div>
              </div>

              <p className="text-sm text-slate-300 text-center italic">
                "{analysis.feedback}"
              </p>
              
              <p className="text-xs text-slate-400 text-center mt-3">
                This is an AI-generated preliminary assessment. Our team will conduct a thorough review.
              </p>
            </div>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-slate-800/30 rounded-lg p-4 text-center">
              <Clock className="h-8 w-8 text-blue-400 mx-auto mb-2" />
              <h3 className="text-white font-medium mb-1">Review Time</h3>
              <p className="text-sm text-slate-400">3-5 business days</p>
            </div>
            <div className="bg-slate-800/30 rounded-lg p-4 text-center">
              <Mail className="h-8 w-8 text-blue-400 mx-auto mb-2" />
              <h3 className="text-white font-medium mb-1">Check Your Email</h3>
              <p className="text-sm text-slate-400">We'll notify you at your email</p>
            </div>
          </div>

          <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
            <h3 className="text-blue-400 font-medium mb-2">What happens next?</h3>
            <ol className="space-y-2 text-sm text-slate-300">
              <li className="flex items-start">
                <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">1</span>
                <span>Our team reviews your application and AI assessment</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">2</span>
                <span>If shortlisted, we'll schedule a brief interview</span>
              </li>
              <li className="flex items-start">
                <span className="bg-blue-500/20 text-blue-400 rounded-full w-5 h-5 flex items-center justify-center text-xs mr-2 mt-0.5 flex-shrink-0">3</span>
                <span>Approved squads receive their squad code and onboarding</span>
              </li>
            </ol>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
            <Button asChild variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800">
              <Link to="/forge">
                <Home className="mr-2 h-4 w-4" />
                Back to Forge
              </Link>
            </Button>
            <Button asChild className="bg-blue-500 hover:bg-blue-600">
              <Link to="/forge/auth">
                Go to Login
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}